// auth.service.ts - Nur die geänderten Methoden

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, lastValueFrom, Observable, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import { Store } from '@ngxs/store';
import { SetCurrentUser } from '../../shared/state/user.actions';
import { MessageService } from 'primeng/api';

@Injectable({ providedIn: 'root' })
export class AuthService {
    constructor(private router: Router, private http: HttpClient, private store: Store, private message: MessageService) {
        // Token-Validierung bei Start
        const storedData = this.getStoredUserData();
        if (storedData && this.isTokenExpired(storedData.timestamp)) {
            this.clearStorage();
            this.userSubject = new BehaviorSubject<User>(null);
        } else {
            this.userSubject = new BehaviorSubject<User>(storedData?.user || null);
        }
        this.user = this.userSubject.asObservable();
    }

    public userSubject: BehaviorSubject<User>;
    public user: Observable<User>;
    public userData;

    public get userValue(): User {
        const storedData = this.getStoredUserData();
        if (storedData && !this.isTokenExpired(storedData.timestamp)) {
            return storedData.user;
        }
        return null;
    }

    private getStoredUserData(): { user: User, timestamp: number } | null {
        const data = localStorage.getItem('currentUser');
        if (data) {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }
        return null;
    }

    private isTokenExpired(timestamp: number): boolean {
        // Token ist 1 Stunde gültig (3600000 ms)
        return timestamp + 3600000 < Date.now();
    }

    private clearStorage(): void {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentUserData');
    }

    logout(): Observable<any> {
        console.log('Logout');
        // Storage leeren
        this.clearStorage();
        this.userSubject.next(null);
        this.store.dispatch(new SetCurrentUser(null as any));
        return this.http.get(`${environment.appApi}/user/logout`);
    }


    async login(username: string, password: string): Promise<any> {
        const body = new FormData();
        body.append('username', username);
        body.append('password', password);
        body.append('grant_type', 'password');
        body.append('client_id', environment.client_id);
        body.append('client_secret', environment.client_secret);
        const request$ = this.http.post(`${environment.appApi}/oauth/token`, body).pipe(take(1));
        const response = await lastValueFrom(request$);

        // Token mit Timestamp in localStorage speichern
        const dataToStore = {
            user: response as User,
            timestamp: Date.now()
        };
        localStorage.setItem('currentUser', JSON.stringify(dataToStore));
        this.userSubject.next(response as User);
        return response;
    }

    // User profile
    getUserProfile(): Observable<any> {
        const api = `${environment.appApi}/user`;
        return this.http.get(api).pipe(
            map((res: Response) => {
                return res || {};
            })
        );
    }

    public getUser(): Promise<User> {
        return new Promise((resolve, reject) => {
            firstValueFrom(this.http.get(`${environment.appApi}/user`)).then(
                (data: User) => {
                    resolve(data);
                },
                (err) => reject(err)
            );
        });
    }

    showError(msg: string): void {
        this.message.add({
            key: 'msgs',
            severity: 'error',
            summary: $localize`:@@error:Fehler!`,
            detail: msg,
        });
    }
}
